import React, { memo, useMemo } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { Button, Breadcrumb } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { ReactComponent as Geocloud } from '~/assets/svg/geocloud_logo.svg';
import { useAtom } from 'jotai';
import { userInformationAtom } from '~/stores/UserStore';
import { headerBreadcrumbsAtom } from '~/stores/BreadcrumbStore';
import Avatar from '../components/Header/components/Avatar';
import NotificationBell from '../components/Header/components/notification/NotificationBell';
import SwitcherLng from '~/components/common/SwitcherLng';
import Routes from '~/routes/routes';
import { getFallbackBreadcrumbs } from '~/utils/breadcrumbUtils';

interface AppHeaderProps {
  onToggleMobileSidebar?: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({ onToggleMobileSidebar }) => {
  const [userInformation] = useAtom(userInformationAtom);
  const [customBreadcrumbs] = useAtom(headerBreadcrumbsAtom);
  const location = useLocation();

  const breadcrumbs = useMemo(() => {
    if (customBreadcrumbs && customBreadcrumbs.length > 0) {
      return customBreadcrumbs;
    }
    return getFallbackBreadcrumbs(location.pathname);
  }, [customBreadcrumbs, location.pathname]);

  return (
    <UI_HeaderWrapper>
      <UI_LeftSection>
        {onToggleMobileSidebar && (
          <UI_MobileMenuBtn
            type="text"
            icon={<MenuOutlined />}
            onClick={onToggleMobileSidebar}
            aria-label="Toggle menu"
          />
        )}
        <UI_BrandLink to={Routes.PROJECT}>
          <Geocloud style={{ width: 28, height: 28 }} />
          <UI_BrandTitle>Geocloud</UI_BrandTitle>
        </UI_BrandLink>

        {breadcrumbs && breadcrumbs.length > 0 && (
          <>
            <UI_BreadcrumbDivider />
            <UI_HeaderBreadcrumb
              items={breadcrumbs}
              separator={<span className="breadcrumb-separator">/</span>}
            />
          </>
        )}
      </UI_LeftSection>

      <UI_RightSection>
        {userInformation && (
          <>
            <NotificationBell />
            <UI_Separator />
          </>
        )}
        <SwitcherLng theme="light" />
        <UI_Separator />
        <Avatar showName={true} />
      </UI_RightSection>
    </UI_HeaderWrapper>
  );
};

export default memo(AppHeader);

const UI_HeaderWrapper = styled.header`
  width: 100%;
  height: var(--header-height);
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(59, 130, 246, 0.1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 4px 12px rgba(59, 130, 246, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  position: relative;
  z-index: 100;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0 10px;
  }
`;

const UI_LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
  overflow: hidden;
`;

const UI_MobileMenuBtn = styled(Button)`
  display: none;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  flex-shrink: 0;

  @media (max-width: 768px) {
    display: inline-flex;
  }
`;

const UI_BrandLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: inherit;
  user-select: none;
  flex-shrink: 0;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.85;
  }
`;

const UI_BrandTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const UI_BreadcrumbDivider = styled.div`
  width: 1px;
  height: 16px;
  background: #cbd5e1;
  margin: 0 4px;
  flex-shrink: 0;

  @media (max-width: 640px) {
    display: none;
  }
`;

const UI_HeaderBreadcrumb = styled(Breadcrumb)`
  font-size: 13px;
  min-width: 0;
  flex-shrink: 1;

  ol {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    margin: 0;
    padding: 0;
  }

  li {
    display: inline-flex;
    align-items: center;
    min-width: 0;
  }

  .ant-breadcrumb-link {
    color: #64748b;
    font-size: 13px;
    display: inline-flex;
    align-items: center;
    max-width: 220px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    a {
      color: #64748b;
      text-decoration: none;
      display: inline-flex;
      align-items: center;
      transition: color 0.15s ease;

      &:hover {
        color: #2563eb;
      }
    }

    svg {
      font-size: 14px;
      width: 14px;
      height: 14px;
      flex-shrink: 0;
    }
  }

  li:last-child .ant-breadcrumb-link {
    color: #0f172a;
    font-weight: 600;
  }

  .breadcrumb-separator,
  .ant-breadcrumb-separator {
    color: #cbd5e1;
    font-size: 12px;
    margin: 0 6px;
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
  }

  @media (max-width: 640px) {
    display: none;
  }
`;

const UI_RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const UI_Separator = styled.div`
  width: 1px;
  height: 18px;
  background: #e2e8f0;
  margin: 0 4px;

  @media (max-width: 640px) {
    display: none;
  }
`;

